/*
  Pre-Black Friday early access page (sections/bfcm-early-access.liquid): the countdown through a schedule of end dates
  (data-ends, one "2026-11-04 00:00" per line in the data-timezone time zone: counts to the next one, shows the live
  text for data-live-hours after it, then resets to the following date by itself; after the last one the live text
  stays), the right video file for the screen size, the email placeholder on Klaviyo's field, and the phone sticky
  bar (countdown + button that scrolls to the form and focuses the email field) - always shown, except while the
  email field is being typed in, when the phone keyboard would push it over the form - the sign-up tracking (Meta
  Lead to the page's pixel when someone signs up), and the sign-up states (Klaviyo's form, our fallback field when
  Klaviyo doesn't show it, "Thanks for signing up!" after a sign-up in this browser).
*/
if (!customElements.get('bfcm-early-access')) {
  customElements.define(
    'bfcm-early-access',
    class BfcmEarlyAccess extends HTMLElement {
      connectedCallback() {
        this.timeZone = this.dataset.timezone || 'America/Los_Angeles';
        this.ends = (this.dataset.ends || '')
          .split(/[\n,]+/)
          .map((line) => this.parseDate(line.trim()))
          .filter((time) => time !== null)
          .sort((a, b) => a - b);
        this.liveMs = (parseFloat(this.dataset.liveHours) || 0) * 3600000;
        this.count = this.querySelector('[data-bfea-count]');
        this.sticky = this.querySelector('[data-bfea-sticky]');
        this.form = this.querySelector('[data-bfea-form]');

        this.setVideoSource();
        this.tick();
        this.timer = window.setInterval(() => this.tick(), 1000);

        this.querySelector('[data-bfea-scroll]')?.addEventListener('click', () => this.scrollToForm());
        this.watchForm();
        this.setPlaceholder();
        this.trackSignups();
        this.setupFallback();
      }

      // ---- Sign-up states: Klaviyo's form / our fallback field / "Thanks for signing up!" ----
      // Signed up in this browser before -> the confirmation (with a link back to a form). Otherwise Klaviyo's form, or
      // our fallback field if Klaviyo hasn't drawn its form a few seconds after load (hidden from known profiles, after
      // a sign-up, or blocked). The fallback adds the email to the same Klaviyo list through Klaviyo's public API.
      setupFallback() {
        this.klaviyoBox = this.querySelector('[data-bfea-klaviyo]');
        this.fallback = this.querySelector('[data-bfea-fallback]');
        this.done = this.querySelector('[data-bfea-done]');
        this.note = this.querySelector('[data-bfea-note]');
        this.error = this.querySelector('[data-bfea-error]');
        if (!this.fallback || !this.done) return;

        this.querySelector('[data-bfea-again]')?.addEventListener('click', () => this.showForm(true));
        this.fallback.addEventListener('submit', (event) => this.submitFallback(event));

        if (this.remembered()) this.showDone();
        else this.showForm(false);
      }

      klaviyoShown() {
        return !!(this.klaviyoBox && this.klaviyoBox.querySelector('input, button'));
      }

      // Klaviyo's form if it draws one within a few seconds of the page load, our field otherwise. If Klaviyo's form
      // turns up later and nobody has started typing in our field, switch to Klaviyo's (it has Kate's next steps).
      showForm(focus) {
        this.done.hidden = true;
        this.form.hidden = false;
        if (this.note) this.note.hidden = false;
        window.clearInterval(this.waitTimer);
        const input = this.fallback.querySelector('input');
        const useKlaviyo = () => {
          this.fallback.hidden = true;
          if (this.klaviyoBox) this.klaviyoBox.hidden = false;
        };
        const useFallback = () => {
          this.fallback.hidden = false;
          if (this.klaviyoBox) this.klaviyoBox.hidden = true;
        };
        const untouched = () => !input.value && document.activeElement !== input;
        const started = Date.now();
        // Waited long enough for Klaviyo: 3s after the page finished loading, and never more than 8s
        const waitedEnough = () => (this.loadedAt && Date.now() - this.loadedAt > 3000) || Date.now() - started > 8000;
        if (!this.loadedAt) {
          if (document.readyState === 'complete') this.loadedAt = Date.now();
          else window.addEventListener('load', () => { this.loadedAt = Date.now(); }, { once: true });
        }
        let tries = 0;
        const decide = () => {
          tries += 1;
          if (this.klaviyoShown()) {
            if (this.fallback.hidden || untouched()) useKlaviyo();
            window.clearInterval(this.waitTimer);
          } else if (focus || waitedEnough()) {
            if (this.fallback.hidden) useFallback();
          }
          if (tries > 120) window.clearInterval(this.waitTimer); // stop watching after ~30s
        };
        decide();
        this.waitTimer = window.setInterval(decide, 250);
        if (focus) window.setTimeout(() => this.form.querySelector('[data-bfea-klaviyo]:not([hidden]) input, [data-bfea-fallback]:not([hidden]) input')?.focus(), 300);
      }

      showDone() {
        window.clearInterval(this.waitTimer);
        this.form.hidden = true;
        if (this.note) this.note.hidden = true;
        if (this.error) this.error.hidden = true;
        this.done.hidden = false;
      }

      storageKey() {
        return `bfea-signed-up-${this.dataset.formId || 'form'}`;
      }

      remember() {
        try {
          localStorage.setItem(this.storageKey(), String(Date.now()));
        } catch (error) {
          /* private mode: no memory, the form just shows next time */
        }
      }

      remembered() {
        try {
          return !!localStorage.getItem(this.storageKey());
        } catch (error) {
          return false;
        }
      }

      async submitFallback(event) {
        event.preventDefault();
        const input = this.fallback.querySelector('input[type="email"]');
        const button = this.fallback.querySelector('button');
        const email = (input.value || '').trim();
        if (this.error) this.error.hidden = true;
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
          input.focus();
          if (this.error) this.error.hidden = false;
          return;
        }
        const company = this.dataset.klaviyoCompany;
        const list = this.dataset.klaviyoList;
        button.disabled = true;
        try {
          if (!company || !list) throw new Error('missing Klaviyo list');
          const response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(company)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/vnd.api+json', revision: '2024-10-15' },
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: {
                  custom_source: this.dataset.leadName || 'Early Access',
                  profile: { data: { type: 'profile', attributes: { email } } },
                },
                relationships: { list: { data: { type: 'list', id: list } } },
              },
            }),
          });
          if (!response.ok) throw new Error(`Klaviyo ${response.status}`);
          this.signedUp();
          this.showDone();
          input.value = '';
        } catch (error) {
          if (this.error) this.error.hidden = false;
        } finally {
          button.disabled = false;
        }
      }

      // Klaviyo fires "klaviyoForms" on window; "submit" = the email was submitted in our form (Klaviyo then shows its
      // own next step). The fallback field calls signedUp() itself.
      trackSignups() {
        const formId = this.dataset.formId;
        if (!formId) return;
        this.onKlaviyo = (event) => {
          const detail = event.detail || {};
          if (detail.type === 'submit' && detail.formId === formId) this.signedUp();
        };
        window.addEventListener('klaviyoForms', this.onKlaviyo);
      }

      // Once per page: Meta Lead to the page's pixel only (trackSingle, so the store's other pixels don't get it), a
      // dataLayer event, and remember the sign-up in this browser.
      signedUp() {
        this.remember();
        if (this.leadSent) return;
        this.leadSent = true;
        const eventId = `ea_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const name = this.dataset.leadName || 'Early Access';
        const pixel = this.dataset.metaPixel;
        if (pixel) this.sendMetaLead(pixel, name, eventId);
        (window.dataLayer = window.dataLayer || []).push({ event: 'early_access_signup', content_name: name, event_id: eventId });
      }

      // Sent through the store's own Meta pixel set-up (Shopify's Facebook & Instagram app sets the pixel up on the
      // page, with its consent handling): wait up to 5s for it, and only set the pixel up here if it never appears.
      sendMetaLead(pixel, name, eventId) {
        const ready = () => !!(window.fbq && window.fbq.instance && window.fbq.instance.pixelsByID && window.fbq.instance.pixelsByID[pixel]);
        const send = () => window.fbq('trackSingle', pixel, 'Lead', { content_name: name, content_category: 'waitlist' }, { eventID: eventId });
        if (ready()) {
          send();
          return;
        }
        const started = Date.now();
        const timer = window.setInterval(() => {
          if (ready()) {
            window.clearInterval(timer);
            send();
          } else if (Date.now() - started > 5000) {
            window.clearInterval(timer);
            if (!window.fbq) {
              /* eslint-disable */
              !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
              /* eslint-enable */
            }
            window.fbq('init', pixel);
            send();
          }
        }, 200);
      }

      // Klaviyo draws the form after load; give its email field the page's placeholder
      setPlaceholder() {
        const text = this.dataset.placeholder;
        if (!text || !this.form) return;
        const apply = () => {
          const input = this.form.querySelector('input[type="email"]');
          if (input && input.placeholder !== text) input.placeholder = text;
        };
        apply();
        this.formObserver = new MutationObserver(apply);
        this.formObserver.observe(this.form, { childList: true, subtree: true });
      }

      disconnectedCallback() {
        window.clearInterval(this.timer);
        this.formObserver?.disconnect();
        window.clearInterval(this.waitTimer);
        if (this.onKlaviyo) window.removeEventListener('klaviyoForms', this.onKlaviyo);
      }

      setVideoSource() {
        const video = this.querySelector('video[data-src-desktop]');
        if (!video) return;
        const mobile = window.matchMedia('(max-width: 767px)').matches;
        video.src = (mobile && video.dataset.srcMobile) || video.dataset.srcDesktop;
        video.play?.().catch(() => {});
      }

      // "2026-11-04" or "2026-11-04 09:30" -> that wall-clock time in the page's time zone (daylight saving included)
      parseDate(text) {
        const match = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2}))?$/);
        if (!match) return null;
        const [, year, month, day, hour = '0', minute = '0'] = match;
        const wall = Date.UTC(+year, +month - 1, +day, +hour, +minute);
        let time = wall - this.offset(wall);
        time = wall - this.offset(time);
        return Number.isNaN(time) ? null : time;
      }

      // How far ahead of UTC the time zone is at a moment, in ms (e.g. Pacific in November: -8 hours)
      offset(time) {
        const parts = {};
        new Intl.DateTimeFormat('en-US', {
          timeZone: this.timeZone,
          hourCycle: 'h23',
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
        })
          .formatToParts(new Date(time))
          .forEach(({ type, value }) => {
            parts[type] = value;
          });
        const local = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour % 24, +parts.minute);
        return local - Math.floor(time / 60000) * 60000;
      }

      // The date being counted to: the first one whose live window hasn't finished. null = past the whole schedule.
      currentEnd(now) {
        return this.ends.find((end) => now < end + this.liveMs) ?? null;
      }

      tick() {
        if (!this.ends.length) return;
        const now = Date.now();
        const end = this.currentEnd(now);
        const live = end === null || now >= end;

        this.count?.classList.toggle('is-live', live);
        const units = this.count?.querySelector('.bfea-count__units');
        const liveText = this.count?.querySelector('[data-bfea-live]');
        if (units) units.hidden = live;
        if (liveText) liveText.hidden = !live;

        if (live) {
          this.setText('[data-bfea-compact]', this.dataset.liveLabel || '');
          return;
        }

        const left = end - now;
        const pad = (n) => String(n).padStart(2, '0');
        const days = Math.floor(left / 86400000);
        const hours = Math.floor((left % 86400000) / 3600000);
        const minutes = Math.floor((left % 3600000) / 60000);

        this.setText('[data-bfea-days]', pad(days));
        this.setText('[data-bfea-hours]', pad(hours));
        this.setText('[data-bfea-minutes]', pad(minutes));
        this.setText('[data-bfea-compact]', `${days}d ${pad(hours)}h ${pad(minutes)}m`);
      }

      setText(selector, text) {
        const element = this.querySelector(selector);
        if (element && element.textContent !== text) element.textContent = text;
      }

      // Phones only (the bar is hidden on desktop in CSS)
      watchForm() {
        if (!this.sticky || !this.form) return;
        const toggle = (show) => {
          this.sticky.classList.toggle('is-visible', show);
          this.sticky.setAttribute('aria-hidden', String(!show));
          this.sticky.querySelector('button')?.setAttribute('tabindex', show ? '0' : '-1');
        };
        toggle(true);
        this.form.addEventListener('focusin', () => toggle(false));
        this.form.addEventListener('focusout', () => toggle(true));
      }

      scrollToForm() {
        const top = window.scrollY + this.form.getBoundingClientRect().top - 140;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        window.setTimeout(() => {
          const input = this.form.querySelector('input[type="email"], input');
          try {
            input?.focus({ preventScroll: true });
          } catch (error) {
            input?.focus();
          }
        }, 450);
      }
    }
  );
}
